/**
 * Menu Configuration
 * Defines all menu sections, items, icons, roles, paths, and access conditions
 * Preserves all existing access control logic from hardcoded LeftNav
 */

import DashboardIcon from '@mui/icons-material/Dashboard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StorageIcon from '@mui/icons-material/Storage';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SettingsIcon from '@mui/icons-material/Settings';
import RadioIcon from '@mui/icons-material/Radio';
import CropFreeIcon from '@mui/icons-material/CropFree';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MessageIcon from '@mui/icons-material/Message';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import TimelineIcon from '@mui/icons-material/Timeline';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

import customConfig from '../config';

/**
 * Helper to check if user can access a menu item
 * @param {Object} item - Menu item with condition function
 * @param {Object} context - { userLevel, roles, tenantId }
 * @returns {boolean}
 */
export const canAccessMenuItem = (item, context) => {
  // If no condition specified, allow access
  if (!item.condition) {
    return true;
  }

  // Execute the condition function with user context
  return item.condition(context);
};

/**
 * Main menu configuration
 * Structure: sections with subitems, each with condition functions preserving original logic
 */
export const menuConfig = [
  {
    id: 'users',
    name: 'Users',
    icon: PeopleIcon,
    path: null,
    condition: () => true, // Always show section
    subitems: [
      {
        id: 'user-details',
        name: 'User Details',
        path: '/dashboard',
        icon: AccountCircleIcon,
        // Simplified: show to all authenticated users (dashboard accessible to all)
        // condition: () => true
        condition: ({ userLevel, roles }) =>
        roles && !roles.includes(customConfig.roleLineTestUser) &&
        !(
          (userLevel === 0 && roles.includes(customConfig.roleFactoryAdmin)) ||
          roles.includes(customConfig.roleFieldOperator)
        )
      },
      {
        id: 'notifications',
        name: 'Notifications',
        path: '/notification',
        icon: NotificationsIcon,
        // Original logic: userLevel >= 2
        condition: ({ userLevel }) => userLevel >= 2
      },
      {
        id: 'feedback',
        name: 'Feedback',
        path: '/feedback',
        icon: MessageIcon,
        // Original logic: userLevel > 3 && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          userLevel > 3 && !roles.includes(customConfig.roleFieldOperator)
      }
    ]
  },
  {
    id: 'devices',
    name: 'Devices',
    icon: DashboardIcon,
    path: null, // Section header, not directly clickable
    condition: () => true, // Always show section header
    subitems: [
      {
        id: 'device-details',
        name: 'Device Details',
        path: '/dashboard',
        icon: StorageIcon,
        // condition: ({ userLevel, roles }) =>
        //   userLevel > 0 && !roles.includes(customConfig.roleFieldOperator)
        condition: ({ userLevel, roles }) =>
        roles && roles.includes(customConfig.roleLineTestUser) ||
        (
          (userLevel === 0 && roles.includes(customConfig.roleFactoryAdmin)) ||
          roles.includes(customConfig.roleFieldOperator)
        )
      },
      {
        id: 'device-details',
        name: 'Device Details',
        path: '/searchDevice',
        icon: StorageIcon,
        // Original logic: userLevel > 0 && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          userLevel > 0 && !roles.includes(customConfig.roleFieldOperator)
      },
      {
        id: 'device-model',
        name: 'Device Model',
        path: '/deviceDetail',
        icon: PhoneAndroidIcon,
        // Original logic: (userLevel > 0 || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel > 0 || roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      },
      {
        id: 'manufactured-devices',
        name: 'Manufactured Devices',
        path: '/manufacturedDevices',
        icon: AllInboxIcon,
        // Original logic: (userLevel > 4 || roleCloudAdmin || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel > 4 ||
            roles.includes(customConfig.roleCloudAdmin) ||
            roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      },
      {
        id: 'device-firmware',
        name: 'Device Firmware',
        path: '/deviceFirmware',
        icon: FlashOnIcon,
        // Original logic: userLevel > 4 && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          userLevel > 4 && !roles.includes(customConfig.roleFieldOperator)
      },
      /**
       * Making it comment out as per discussion.
       */
      // {
      //   id: 'manage-mqtt-fota',
      //   name: 'Manage MQTT FOTA',
      //   path: '/fotaJobs',
      //   icon: ShowChartIcon,
      //   // Original logic: userLevel > 4 && !roleFieldOperator
      //   condition: ({ userLevel, roles }) =>
      //     userLevel > 4 && !roles.includes(customConfig.roleFieldOperator)
      // }
    ]
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: BuildIcon,
    path: null,
    condition: () => true, // Always show section
    subitems: [
      {
        id: 'idu-eeprom-config',
        name: 'IDU EEPROM Config',
        path: '/idueepromConfig',
        icon: SettingsIcon,
        // Original logic: (userLevel > 4 || roleCloudAdmin || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel > 4 ||
            roles.includes(customConfig.roleCloudAdmin) ||
            roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      },
      {
        id: 'matter-cd',
        name: 'Matter CD',
        path: '/MatterCD',
        icon: RadioIcon,
        // Original logic: userLevel > 4 || roleCloudAdmin
        condition: ({ userLevel, roles }) =>
          userLevel > 4 || roles.includes(customConfig.roleCloudAdmin)
      },
      {
        id: 'qr-code',
        name: 'QR Code',
        path: '/qrcode',
        icon: CropFreeIcon,
        // Original logic: (userLevel > 4 || roleCloudAdmin || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel > 4 ||
            roles.includes(customConfig.roleCloudAdmin) ||
            roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      },
      {
        id: 'dac-statistics',
        name: 'DAC Statistics',
        path: '/DACStatistics',
        icon: AssessmentIcon,
        // Original logic: roleFactoryAdmin || roleCustomerSupportL4 || roleCloudAdmin
        condition: ({ roles }) =>
          (roles.includes(customConfig.roleFactoryAdmin) ||
          roles.includes(customConfig.roleCustomerSupportL4) ||
          roles.includes(customConfig.roleCloudAdmin)) && !roles.includes(customConfig.roleLineTestUser)
      }
    ]
  },
  {
    id: 'statistics',
    name: 'Statistics',
    icon: TrendingUpIcon,
    path: null,
    condition: () => true, // Section always visible
    subitems: [
      {
        id: 'device-statistics',
        name: 'Device Statistics',
        path: '/statistics',
        icon: PieChartIcon,
        // Original logic: userLevel > 2 && tenantId === "panasonic" && !roleFieldOperator
        condition: ({ userLevel, roles, tenantId }) =>
          userLevel > 2 &&
          tenantId === 'panasonic' &&
          !roles.includes(customConfig.roleFieldOperator)
      },
      {
        id: 'product-metrics',
        name: 'Product Metrics',
        path: '/productMetrics',
        icon: TimelineIcon,
        // Original logic: userLevel > 2 && tenantId === "panasonic" && !roleFieldOperator
        condition: ({ userLevel, roles, tenantId }) =>
          userLevel > 2 &&
          tenantId === 'panasonic' &&
          !roles.includes(customConfig.roleFieldOperator)
      }
    ]
  },
  {
    id: 'others',
    name: 'Others',
    icon: CategoryIcon,
    path: null,
    condition: () => true, // Section always visible
    subitems: [
      {
        id: 'dealer-management',
        name: 'Dealer Management',
        path: '/deviceDealer',
        icon: SupervisorAccountIcon,
        // Original logic: (userLevel > 4 || roleCloudAdmin || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel > 4 ||
            roles.includes(customConfig.roleCloudAdmin) ||
            roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      },

      /** 
       * The LoyaltySpec feature has been temporarily commented out because the corresponding backend services are not yet available in the 
       * staging environment. As part of the release from develop to staging, we have disabled it for now. The feature will be enabled once the 
       * backend services are deployed and ready..
      */
      {
        id: 'loyalty-management',
        name: 'Loyalty Management',
        path: '/loyaltySpecs',
        icon: CardGiftcardIcon,
        // Original logic: (userLevel >= 3 || roleCloudAdmin || roleFactoryAdmin) && !roleFieldOperator
        condition: ({ userLevel, roles }) =>
          (userLevel >= 3 ||
            roles.includes(customConfig.roleCloudAdmin) ||
            roles.includes(customConfig.roleFactoryAdmin)) &&
          !roles.includes(customConfig.roleFieldOperator) && !roles.includes(customConfig.roleLineTestUser)
      }
    ]
  }
];

/**
 * Filter menu to only show accessible items for current user
 * @param {Array} menu - Full menu config
 * @param {Object} context - { userLevel, roles, tenantId }
 * @returns {Array} Filtered menu with only accessible sections and items
 */
export const getAccessibleMenu = (menu, context) => {
  return menu
    .map(section => {
      // Check if section is accessible
      if (!canAccessMenuItem(section, context)) {
        return null;
      }

      // Filter subitems
      const accessibleSubitems = section.subitems
        ? section.subitems.filter(item => canAccessMenuItem(item, context))
        : [];

      // Return section only if it has accessible subitems or no subitems condition
      if (accessibleSubitems.length > 0 || !section.subitems) {
        return {
          ...section,
          subitems: accessibleSubitems
        };
      }

      return null;
    })
    .filter(Boolean);
};
